/**
 * NIGHTBOUND - future systems: wild creatures.
 *
 * Data definitions only. Wild creatures are not reachable in this milestone;
 * Adventure is a locked door. Shipping the definitions now means the schema,
 * the asset folders and the encounter tables already agree with each other.
 */

import type { StatusEffectId } from '../abilities'

export const WILD_KEYS = ['WOLF', 'LYNX', 'CROW', 'VIPER'] as const
export type WildKey = (typeof WILD_KEYS)[number]

export interface WildCreatureDefinition {
  key: WildKey
  name: string
  family: 'Canine' | 'Feline' | 'Avian' | 'Reptile'
  role: string
  description: string
  /** Relative power bracket used by future encounter balancing. */
  tier: 1 | 2 | 3 | 4
  baseStats: {
    hp: number
    attack: number
    defence: number
    speed: number
  }
  /** Ability this creature uses in combat. */
  abilityKey: string
  /** Ability the player's creature can learn from its Essence. */
  essenceAbilityKey: string
  /** Mutation unlocked at the Essence mutation threshold. */
  mutationKey: string
  /** Status effect this creature is known for. */
  signatureEffect: StatusEffectId | null
  /** Asset folder under public/assets/wild/. */
  assetKey: string
  /** How much raw flesh devouring one yields (future Hunger restore). */
  devourHungerValue: number
  /** Sanity cost of devouring it (future). */
  devourSanityCost: number
  /** Essence granted per kill. */
  essencePerKill: number
  /** Where it is likely to be encountered. */
  habitats: string[]
}

export const WILD_CREATURES: Record<WildKey, WildCreatureDefinition> = {
  WOLF: {
    key: 'WOLF',
    name: 'Wolf',
    family: 'Canine',
    role: 'Physical attacker',
    description:
      'Lean and wrong-jawed, it hunts in twos and remembers every wound you gave it.',
    tier: 2,
    baseStats: { hp: 130, attack: 16, defence: 10, speed: 13 },
    abilityKey: 'savage_bite',
    essenceAbilityKey: 'savage_bite',
    mutationKey: 'lupine',
    signatureEffect: 'BLEED',
    assetKey: 'wolf',
    devourHungerValue: 45,
    devourSanityCost: 6,
    essencePerKill: 1,
    habitats: ['Back Alleys', 'Frozen Yard'],
  },
  LYNX: {
    key: 'LYNX',
    name: 'Lynx',
    family: 'Feline',
    role: 'Critical attacker',
    description:
      'It does not hunt so much as arrive. You will hear it only after it has decided.',
    tier: 3,
    baseStats: { hp: 105, attack: 19, defence: 8, speed: 19 },
    abilityKey: 'ambush',
    essenceAbilityKey: 'ambush',
    mutationKey: 'predator_eyes',
    signatureEffect: null,
    assetKey: 'lynx',
    devourHungerValue: 38,
    devourSanityCost: 5,
    essencePerKill: 1,
    habitats: ['Collapsed Overpass', 'Ruined Chapel'],
  },
  CROW: {
    key: 'CROW',
    name: 'Crow',
    family: 'Avian',
    role: 'Speed / evasion',
    description:
      'One crow is a bird. Eleven crows is a decision that has already been made about you.',
    tier: 1,
    baseStats: { hp: 70, attack: 12, defence: 6, speed: 22 },
    abilityKey: 'dive',
    essenceAbilityKey: 'dive',
    mutationKey: 'wings',
    signatureEffect: 'SLOW',
    assetKey: 'crow',
    devourHungerValue: 22,
    devourSanityCost: 4,
    essencePerKill: 1,
    habitats: ['Back Alleys', 'Hanging Trees'],
  },
  VIPER: {
    key: 'VIPER',
    name: 'Viper',
    family: 'Reptile',
    role: 'Poison / status',
    description:
      'It has been under the floor the whole time. It has been patient about it.',
    tier: 3,
    baseStats: { hp: 95, attack: 14, defence: 11, speed: 15 },
    abilityKey: 'venom_fang',
    essenceAbilityKey: 'venom_fang',
    mutationKey: 'venom_glands',
    signatureEffect: 'POISON',
    assetKey: 'viper',
    devourHungerValue: 30,
    devourSanityCost: 7,
    essencePerKill: 1,
    habitats: ['Drain Tunnels', 'Ruined Chapel'],
  },
}

export const WILD_LIST: WildCreatureDefinition[] = WILD_KEYS.map((k) => WILD_CREATURES[k])
